/**
 * Reward config editing commands (R7.9/R7.12, RS3 in `docs/specs/
 * phase-2a-rewards.md`). Pure functions returning the event(s) to append —
 * shares the `CommandResult`/`DomainCommandError` shape from
 * `../event-log/command-error` with every other commands module. No I/O
 * (ADR-0001 rule 2).
 *
 * `reward_config.changed` carries a FULL snapshot, never a diff (ADR-0002:
 * "recompute needs no replay of config deltas, and 'reset to defaults' is
 * just another snapshot event"). Validation here guards the same invariants
 * the settings editor is specified to enforce client-side
 * (`settings-rewards.md` §1.3 "Guardrails") — re-checked here because the
 * domain layer must never trust a caller-supplied config blindly; a
 * malformed curve would corrupt `deriveLevel`'s termination guarantee.
 */

import type {
  BadgeArchivedEvent,
  BadgeCreatedEvent,
  BadgeCriterion,
  BadgeDefinition,
  BadgeEditedEvent,
  BadgeId,
  ClockPort,
  CompassEvent,
  IdPort,
  RewardConfig,
  RewardConfigChangedEvent,
} from '../../contracts';
import { DEFAULT_REWARD_CONFIG, DEFERRED_STARTER_BADGES, STARTER_BADGES } from '../../contracts';
import { type CommandResult, domainError } from '../event-log/command-error';

/**
 * Guards against a config that would break `deriveLevel`'s strictly-
 * increasing-threshold assumption (settings-rewards.md §1.3: "the curve
 * editor enforces strictly increasing thresholds"). XP values are NOT
 * clamped here to the UI's 0-999 range (`settings-rewards.md` §1.3's
 * guardrail is a UI-affordance choice, not a domain invariant — the domain
 * only rejects what would break correctness, e.g. negative XP, which would
 * violate the no-punitive-mechanics rule at the config level).
 */
function assertValidRewardConfig(config: RewardConfig): void {
  for (const [source, value] of Object.entries(config.xp)) {
    if (value < 0) {
      throw domainError(
        'invalid_input',
        `xp.${source} must not be negative (no punitive mechanics, R7.11): got ${value}`,
      );
    }
  }

  const { thresholds } = config.levelCurve;
  for (let i = 0; i < thresholds.length; i += 1) {
    const value = thresholds[i];
    if (value === undefined || value < 0) {
      throw domainError(
        'invalid_input',
        `levelCurve.thresholds[${i}] must be non-negative`,
      );
    }
    if (i > 0) {
      const previous = thresholds[i - 1] ?? 0;
      if (value <= previous) {
        throw domainError(
          'invalid_input',
          `levelCurve.thresholds must be strictly increasing: threshold[${i}] (${value}) <= threshold[${i - 1}] (${previous})`,
        );
      }
    }
  }

  if (config.levelIdentities.length !== thresholds.length + 1) {
    throw domainError(
      'invalid_input',
      `levelIdentities.length (${config.levelIdentities.length}) must equal levelCurve.thresholds.length + 1 (${thresholds.length + 1})`,
    );
  }
}

/** RS3: records a full-snapshot `reward_config.changed` event (R7.9). */
export function updateRewardConfig(
  config: RewardConfig,
  ports: { readonly clock: ClockPort; readonly id: IdPort },
): CommandResult<RewardConfigChangedEvent> {
  assertValidRewardConfig(config);

  const event: RewardConfigChangedEvent = {
    eventId: ports.id.nextEventId(),
    type: 'reward_config.changed',
    schemaVersion: 1,
    occurredOn: ports.clock.today(),
    recordedAt: ports.clock.now(),
    config,
  };

  return { events: [event] };
}

/** RS3: "reset to defaults" is just another snapshot event (ADR-0002). */
export function resetRewardConfig(
  ports: { readonly clock: ClockPort; readonly id: IdPort },
): CommandResult<RewardConfigChangedEvent> {
  return updateRewardConfig(DEFAULT_REWARD_CONFIG, ports);
}

// ---------------------------------------------------------------------------
// Badge CRUD (R7.10 badge editor, RS7's domain dependency) — user-created
// badges beyond the starter set. Structure/validation mirrors the objectives
// commands module's style; badge state (earned/locked/progress) is derived
// separately in badges.ts, never stored.
// ---------------------------------------------------------------------------

/** Union of starter (active + deferred) badge IDs, reserved from user creation. */
function allKnownBadgeIds(events: readonly CompassEvent[]): ReadonlySet<BadgeId> {
  const ids = new Set<BadgeId>([
    ...STARTER_BADGES.map((b) => b.badgeId),
    ...DEFERRED_STARTER_BADGES.map((b) => b.badgeId),
  ]);
  for (const event of events) {
    if (event.type === 'badge.created') ids.add(event.badgeId);
  }
  return ids;
}

function assertValidBadgeContent(input: {
  readonly name: string;
  readonly iconId: string;
}): void {
  if (input.name.trim().length === 0) {
    throw domainError('invalid_input', 'badge name must not be empty');
  }
  if (input.iconId.trim().length === 0) {
    throw domainError('invalid_input', 'badge iconId must not be empty');
  }
}

export function createBadge(
  // Unused by design: unlike editBadge/archiveBadge, creating a new badge
  // needs no existing-badge lookup. Kept as a parameter for call-site
  // symmetry with every other command in this module (mirrors
  // objectives/commands.ts's createObjective, which documents the same
  // pattern for its own unused `_events`).
  _events: readonly CompassEvent[],
  draft: { readonly name: string; readonly iconId: string; readonly criterion: BadgeCriterion },
  ports: { readonly clock: ClockPort; readonly id: IdPort },
): CommandResult<BadgeCreatedEvent> {
  assertValidBadgeContent(draft);

  const badgeId = ports.id.nextRawId() as BadgeId;
  const event: BadgeCreatedEvent = {
    eventId: ports.id.nextEventId(),
    type: 'badge.created',
    schemaVersion: 1,
    occurredOn: ports.clock.today(),
    recordedAt: ports.clock.now(),
    badgeId,
    name: draft.name,
    iconId: draft.iconId,
    criterion: draft.criterion,
  };

  return { events: [event] };
}

export function editBadge(
  events: readonly CompassEvent[],
  badgeId: BadgeId,
  changes: {
    readonly name?: string;
    readonly iconId?: string;
    readonly criterion?: BadgeCriterion;
  },
  ports: { readonly clock: ClockPort; readonly id: IdPort },
): CommandResult<BadgeEditedEvent> {
  requireBadge(events, badgeId);
  if (changes.name !== undefined && changes.name.trim().length === 0) {
    throw domainError('invalid_input', 'badge name must not be empty');
  }
  if (changes.iconId !== undefined && changes.iconId.trim().length === 0) {
    throw domainError('invalid_input', 'badge iconId must not be empty');
  }

  const event: BadgeEditedEvent = {
    eventId: ports.id.nextEventId(),
    type: 'badge.edited',
    schemaVersion: 1,
    occurredOn: ports.clock.today(),
    recordedAt: ports.clock.now(),
    badgeId,
    changes,
  };

  return { events: [event] };
}

export function archiveBadge(
  events: readonly CompassEvent[],
  badgeId: BadgeId,
  ports: { readonly clock: ClockPort; readonly id: IdPort },
): CommandResult<BadgeArchivedEvent> {
  requireBadge(events, badgeId);

  const event: BadgeArchivedEvent = {
    eventId: ports.id.nextEventId(),
    type: 'badge.archived',
    schemaVersion: 1,
    occurredOn: ports.clock.today(),
    recordedAt: ports.clock.now(),
    badgeId,
  };

  return { events: [event] };
}

function requireBadge(events: readonly CompassEvent[], badgeId: BadgeId): void {
  const known = allKnownBadgeIds(events);
  const archived = new Set(
    events
      .filter((e): e is BadgeArchivedEvent => e.type === 'badge.archived')
      .map((e) => e.badgeId),
  );
  if (!known.has(badgeId) || archived.has(badgeId)) {
    throw domainError('badge_not_found', `badge ${badgeId} not found`);
  }
}

/**
 * Assemble the live badge set as of now: STARTER_BADGES (RW-9 active set,
 * habit-independent templates only) plus every non-archived `badge.created`
 * badge, with `badge.edited` changes folded in (last-write-wins by log
 * order). `DEFERRED_STARTER_BADGES` are deliberately excluded — AC-R13's
 * exact assertion is that `listBadges()` never returns a badge whose
 * criterion kind is `checkin_streak_weeks` or `weekly_reviews`.
 */
export function assembleLiveBadges(
  events: readonly CompassEvent[],
): readonly BadgeDefinition[] {
  const archived = new Set(
    events
      .filter((e): e is BadgeArchivedEvent => e.type === 'badge.archived')
      .map((e) => e.badgeId),
  );

  const byId = new Map<BadgeId, BadgeDefinition>();
  for (const badge of STARTER_BADGES) {
    byId.set(badge.badgeId, badge);
  }
  for (const event of events) {
    if (event.type === 'badge.created') {
      byId.set(event.badgeId, {
        badgeId: event.badgeId,
        name: event.name,
        iconId: event.iconId,
        criterion: event.criterion,
      });
    } else if (event.type === 'badge.edited') {
      const existing = byId.get(event.badgeId);
      if (existing) {
        byId.set(event.badgeId, {
          ...existing,
          ...(event.changes.name !== undefined ? { name: event.changes.name } : {}),
          ...(event.changes.iconId !== undefined ? { iconId: event.changes.iconId } : {}),
          ...(event.changes.criterion !== undefined
            ? { criterion: event.changes.criterion }
            : {}),
        });
      }
    }
  }

  return [...byId.values()].filter((badge) => !archived.has(badge.badgeId));
}

/** True for badges defined in `STARTER_BADGES` (drives `BadgeView.isStarter`). */
export function isStarterBadge(badgeId: BadgeId): boolean {
  return STARTER_BADGES.some((b) => b.badgeId === badgeId);
}
